<template>
  <div class="row q-col-gutter-sm">
    <div
      v-for="signature in signatures"
      :key="signature.url"
    >
      <q-card
        flat
        data-e2e="card-signature"
        class="signature-card hover-detect cursor-pointer"
        @click="viewSignature(signature)"
      >
        <div class="row q-pa-sm items-center">
          <div
            class="col q-pr-xs text-caption ellipsis text-dark-variant"
          >
            {{ signature.label }}
          </div>
          <div
            class="col-auto"
          >
            <q-btn
              icon="more_vert"
              size="sm"
              flat
              round
              dense
              :class="{ 'show-on-hover': !isMobileView }"
              @click.stop
            >
              <q-menu>
                <q-list style="min-width: 100px">
                  <q-item
                    v-close-popup
                    clickable
                    class="q-pl-none"
                    @click="viewSignature(signature)"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap items-end"
                    >
                      <q-icon
                        name="preview"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.sessionNotesSignature.viewSignature') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="signature.geolocation"
                    v-close-popup
                    clickable
                    class="q-pl-none"
                    :disable="offlineMode"
                    @click="onOpenMap(signature.geolocation)"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap items-end"
                    >
                      <q-icon
                        name="place"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.sessionNotesSignature.showGeolocation') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canDeleteSignatures"
                    v-close-popup
                    clickable
                    :disable="isActivityRendered"
                    class="q-pl-none"
                    @click="onDeleteSignature(signature)"
                  >
                    <q-tooltip v-if="isActivityRendered">
                      {{ $t('sessions.sessionCard.sessionNotesSignature.unrenderToDelete') }}
                    </q-tooltip>
                    <q-item-section
                      avatar
                      class="text-no-wrap items-end"
                    >
                      <q-icon
                        name="delete"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.sessionNotesSignature.deleteSignature') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
        <div class="signature">
          <q-img
            :src="signature.url"
            height="120px"
            fit="contain"
          />
        </div>
        <q-card-section class="q-pa-sm">
          <q-item class="q-pa-none">
            <q-item-section>
              <q-item-label class="ellipsis-3-lines">
                {{ getSignatoryName(signature) }}
              </q-item-label>
              <q-item-label caption>
                {{ formatDate(signature.timestamp) }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-card-section>
      </q-card>
    </div>
    <div
      v-for="signatory in waitingSignatories"
      :key="'w_'+(signatory.userId || '')"
    >
      <q-card
        v-if="!isSigningInProgress"
        flat
        data-e2e="card-pending-signature"
        class="signature-card items-center"
        :class="{
          'hover-detect': userCanSign(signatory),
          'can-sign': userCanSign(signatory),
          'cursor-pointer': userCanSign(signatory)
        }"
        @click="onSign(signatory)"
      >
        <div class="row q-pa-sm items-center">
          <div
            class="col q-pr-xs text-caption ellipsis text-dark-variant"
          >
            {{ signatory.label }}
          </div>
          <div
            class="col-auto"
          >
            <q-btn
              v-if="canSeeMoreMenu(signatory)"
              icon="more_vert"
              size="sm"
              flat
              round
              dense
              class="show-on-hover"
              @click.stop
            >
              <q-menu>
                <q-list style="min-width: 100px">
                  <q-item
                    v-if="userCanSign(signatory)"
                    v-close-popup
                    clickable
                    class="q-pl-none"
                    @click="onSign(signatory)"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap items-end"
                    >
                      <q-icon
                        name="fas fa-file-signature"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('signature.btnSignNow') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="canCancelSignatureRequest"
                    v-close-popup
                    clickable
                    class="q-pl-none"
                    @click="onCancelSignatureRequest(signatory)"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap items-end"
                    >
                      <q-icon
                        name="cancel"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.modals.signature.btnCancelRequest') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
        <div class="row items-center justify-center signature">
          <q-chip
            color="warning"
            class="remove-on-hover text-white"
          >
            {{ $t('sessions.sessionCard.sessionNotesSignature.pendingSignature') }}
          </q-chip>
          <q-btn
            v-if="userCanSign(signatory)"
            :label="$t('signature.btnSignNow')"
            flat
            color="primary"
            class="add-on-hover"
          />
        </div>
        <q-card-section class="q-pa-sm">
          <q-item class="q-pa-none">
            <q-item-section>
              <q-item-label class="ellipsis-3-lines">
                {{ getSignatoryName(signatory) }}
              </q-item-label>
              <q-item-label caption>
              &nbsp;
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-card-section>
      </q-card>
      <q-card
        v-else
        flat
        class="signature-card items-center"
      >
        <div class="row q-pa-sm items-center">
          <div
            class="col q-pr-xs text-caption ellipsis text-dark-variant"
          >
            {{ signatory.label }}
          </div>
        </div>
        <div class="row items-center justify-center signature">
          <q-btn
            :label="$t('signature.btnSignNow')"
            loading
            flat
            color="primary"
            class="add-on-hover"
          />
        </div>
        <q-card-section class="q-pa-sm">
          <q-item class="q-pa-none">
            <q-item-section>
              <q-item-label class="ellipsis-3-lines">
                {{ getSignatoryName(signatory) }}
              </q-item-label>
              <q-item-label caption>
              &nbsp;
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-card-section>
      </q-card>
    </div>
  </div>
</template>

<script>
import PreviewFile from 'components/ClientFiles/modals/PreviewFile';
import SessionGoogleMapsPoints from 'components/Sessions/SessionGoogleMapsPoints';
import { filter, find, get, isDate, map } from 'lodash';
import { mapState } from 'pinia';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import SessionNotesTemplates from 'src/mixins/SessionNotesTemplates';
import { network } from 'src/store/network';
import { useTime } from 'src/composables/time';

export default {
  name: 'SessionActivitySignatures',

  mixins: [Auth, Interactions, SessionNotesTemplates],

  props: {
    signatures: {
      type: Array,
      required: true,
    },
    requiredSignatories: {
      type: Array,
      required: true,
    },
    isActivityRendered: {
      type: Boolean,
      default: false,
    },
    allowDeleteSignatures: {
      type: Boolean,
      default: true,
    },
    cancelRequestConfirmMessage: {
      type: String,
      default: null,
    },
  },

  emits: ['sign-request', 'delete-signature', 'cancel-signature-request'],

  setup() {
    const { getLocaleDateString } = useTime();
    return { getLocaleDateString };
  },

  data() {
    return {
      isSigningInProgress: false,
    };
  },

  computed: {
    ...mapState(network, ['offlineMode']),

    waitingSignatories() {
      return filter(this.requiredSignatories, { completed: false });
    },

    isMobileView() {
      return this.$q.platform.is.mobile || this.$q.screen.lt.sm;
    },

    canDeleteSignatures() {
      return this.allowDeleteSignatures && this.userHasPrivilege('sessionActivities/deleteSignatures');
    },

    canCancelSignatureRequest() {
      return this.userHasPrivilege('sessionActivities/cancelSignatures');
    },
  },

  methods: {

    viewSignature(selectedSignature) {
      const allFiles = map(this.signatures, (signature, idx) => ({
        ...signature,
        fileType: 'image/png',
        fileName: `${signature.userName} (${this.formatDate(signature.timestamp)})`,
        fileId: idx.toString(),
      }));
      const { fileId } = find(allFiles, { url: selectedSignature.url });
      this.openComponentDialog(PreviewFile, {
        allFiles,
        currentFileId: fileId,
      });
    }, // viewSignature()

    onSign(signatory) {
      if (!this.userCanSign(signatory)) {
        return;
      }

      this.$emit('sign-request', signatory);
    }, // onSign()

    onOpenMap(location) {
      const coords = { lat: location.latitude, lng: location.longitude };
      const title = get(location, 'place') || this.$t('sessions.modals.geolocation.signatureLabel');

      this.openComponentDialog(SessionGoogleMapsPoints, {
        coords,
        title,
      });
    }, // onOpenMap()

    onDeleteSignature(signature) {
      this.confirmDialog(
        null,
        this.$t('sessions.modals.signature.confirmDeleteMessage'),
        () => {
          this.$emit('delete-signature', signature);
        },
      );
    }, // onDeleteSignature()

    onCancelSignatureRequest(signatory) {
      this.confirmDialog(
        null,
        this.cancelRequestConfirmMessage || this.$t('sessions.modals.signature.confirmCancelMessage'),
        () => {
          this.$emit('cancel-signature-request', signatory);
        },
      );
    }, // onCancelSignatureRequest()

    formatDate(date) {
      const inputDate = isDate(date) ? date : date.toDate();

      return this.getLocaleDateString(inputDate, { options: { hour: 'numeric', minute: '2-digit' } });
    }, // formatDate()

    canSeeMoreMenu(signatory) {
      return this.userCanSign(signatory) || this.canCancelSignatureRequest;
    },

    userCanSign(signatory) {
      return (signatory.userId === this.userDetails.userId)
        || this.userHasPrivilege('sessions/signOtherSignatureRequests');
    },

    getSignatoryName(signatory) {
      if (!signatory.professionalCredentials?.length) {
        return signatory.userName;
      }

      return `${signatory.userName}, ${signatory.professionalCredentials.join(', ')}`;
    },

  },
};
</script>

<style lang="scss" scoped>
.signature-card {
  width: 200px;
}

.signature-card.can-sign {

  &:hover {
    background-color: $grey-3;
  }
}

.signature-card .signature {
  height:120px;
}

.hover-detect .show-on-hover {
  visibility: hidden;
}
.hover-detect:hover .show-on-hover {
  visibility: visible;
}

.hover-detect .remove-on-hover {
  display: inherit;
}
.hover-detect:hover .remove-on-hover {
  display: none;
}

.hover-detect .add-on-hover {
  display: none;
}
.hover-detect:hover .add-on-hover {
  display: inherit;
}

</style>
